import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  getNotifPrefs, setNotifPrefs, setEventPref, previewNotify, loadNotifPrefsFromServer,
  NOTIF_SOUNDS, NOTIF_EVENT_KEYS, requestNotifyPermission, notifPermission,
} from "@/lib/notify";
import { Bell, BellOff, Play, Upload, Trash2, Volume2, Music, Inbox, MessageSquare, FileCheck2, Loader2, Smartphone, Send } from "lucide-react";
import { pushSupported, isPushSubscribed, subscribeWebPush, unsubscribeWebPush, sendTestPush } from "@/lib/webpush";
import { requestNotifyPermission as _reqPerm } from "@/lib/notify";

const MAX_BYTES = 1024 * 1024; // 1 MB — notification sounds must stay small

const EVENT_META = {
  order: { label: "Nouvelle demande", sub: "Un client dépose un fichier", Icon: Inbox },
  message: { label: "Nouveau message", sub: "Message dans une conversation", Icon: MessageSquare },
  ready: { label: "Fichier prêt", sub: "Votre fichier traité est disponible", Icon: FileCheck2 },
};

const SOUND_OPTIONS = [
  { id: "auto", label: "Automatique" },
  ...Object.entries(NOTIF_SOUNDS).map(([id, s]) => ({ id, label: s.label })),
];

const EventSoundRow = ({ kind, event, volume, onChange }) => {
  const { label, sub, Icon } = EVENT_META[kind];
  const fileRef = useRef(null);

  const choose = (id) => {
    const next = setEventPref(kind, { sound: id });
    onChange(next);
    previewNotify({ sound: id, volume, customUrl: id === "custom" ? next.events[kind].customUrl : null });
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("audio/")) { toast.error("Choisissez un fichier audio (MP3, WAV, OGG…)."); return; }
    if (f.size > MAX_BYTES) { toast.error("Fichier trop lourd (max 1 Mo). Choisissez un son court."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const next = setEventPref(kind, { sound: "custom", customUrl: reader.result, customName: f.name });
      onChange(next);
      previewNotify({ sound: "custom", volume, customUrl: reader.result });
      toast.success(`Son perso « ${label} » : ${f.name}`);
    };
    reader.onerror = () => toast.error("Lecture du fichier impossible.");
    reader.readAsDataURL(f);
  };

  const removeCustom = () => { const next = setEventPref(kind, { sound: "auto", customUrl: null, customName: "" }); onChange(next); };
  const test = () => previewNotify({ sound: event.sound, volume, customUrl: event.customUrl });

  return (
    <div className="py-4 border-b border-white/10" data-testid={`notif-event-${kind}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-8 h-8 grid place-items-center rounded-sm bg-aurora-cyan/12 text-aurora-cyan shrink-0"><Icon className="w-4 h-4" /></span>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{label}</div>
          <div className="font-mono text-[10px] text-zinc-500 truncate">{sub}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap pl-10">
        <select
          data-testid={`notif-event-${kind}-select`}
          value={event.sound === "custom" ? "custom" : event.sound}
          onChange={(e) => (e.target.value === "custom" ? fileRef.current?.click() : choose(e.target.value))}
          className="bg-black/50 border border-white/12 text-white px-2.5 py-1.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none min-w-[160px]"
        >
          {SOUND_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-ink-base">{o.label}</option>)}
          {event.customUrl && <option value="custom" className="bg-ink-base">🎵 {event.customName || "Mon son"}</option>}
        </select>
        <button data-testid={`notif-event-${kind}-test`} onClick={test}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/15 text-xs hover:border-aurora-cyan/50 hover:text-aurora-cyan transition-colors">
          <Play className="w-3.5 h-3.5" /> Tester
        </button>
        <button data-testid={`notif-event-${kind}-import`} onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/15 text-xs hover:border-aurora-purple/50 hover:text-aurora-purple transition-colors">
          <Upload className="w-3.5 h-3.5" /> MP3 perso
        </button>
        {event.customUrl && (
          <button data-testid={`notif-event-${kind}-remove`} onClick={removeCustom}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/15 text-xs text-zinc-400 hover:border-signal-error/50 hover:text-signal-error transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="audio/*" onChange={onFile} className="hidden" data-testid={`notif-event-${kind}-file`} />
    </div>
  );
};

// Clear on/off switch for background Web Push + a "send test" button.
const PushSection = () => {
  const [supported] = useState(() => pushSupported());
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => { isPushSubscribed().then(setOn).finally(() => setBusy(false)); }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (on) {
        await unsubscribeWebPush();
        setOn(false);
        toast.success("Notifications push désactivées");
      } else {
        if (Notification.permission !== "granted") {
          const r = await _reqPerm();
          if (r !== "granted") { toast.error("Autorisez les notifications dans votre navigateur."); return; }
        }
        const ok = await subscribeWebPush();
        setOn(ok);
        ok ? toast.success("Notifications push activées") : toast.error("Impossible d'activer le push sur cet appareil.");
      }
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    const ok = await sendTestPush();
    setBusy(false);
    ok ? toast.success("Notification test envoyée 📩") : toast.error("Test impossible — activez d'abord le push.");
  };

  if (!supported) return null;

  return (
    <div className="pb-4 mb-2 border-b border-white/10" data-testid="push-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 grid place-items-center rounded-sm bg-aurora-cyan/12 text-aurora-cyan shrink-0"><Smartphone className="w-4 h-4" /></span>
          <div>
            <div className="text-sm font-semibold">Notifications push</div>
            <div className="font-mono text-[10px] text-zinc-500">Recevez les alertes même l'application fermée</div>
          </div>
        </div>
        <button data-testid="push-toggle" onClick={toggle} disabled={busy}
          className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-60 ${on ? "bg-aurora-cyan" : "bg-white/15"}`}>
          {busy
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            : <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-0.5"}`} />}
        </button>
      </div>
      {on && (
        <div className="pl-10 mt-3">
          <button data-testid="push-test-btn" onClick={test} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-aurora-cyan/40 text-aurora-cyan text-xs hover:bg-aurora-cyan/10 transition-colors disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> Envoyer un test
          </button>
        </div>
      )}
    </div>
  );
};

export const NotificationSettings = () => {
  const [prefs, setPrefs] = useState(() => getNotifPrefs());
  const [perm, setPerm] = useState(notifPermission());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifPrefsFromServer().then((p) => setPrefs(p)).finally(() => setLoading(false));
  }, []);

  const update = (patch) => { const next = setNotifPrefs(patch); setPrefs(next); return next; };

  const askPerm = async () => {
    const r = await requestNotifyPermission();
    setPerm(r);
    if (r === "granted") toast.success("Notifications système activées");
    else if (r === "denied") toast.error("Bloqué par le navigateur (réactivez dans les réglages du site).");
  };

  return (
    <div className="glass rounded-md p-6" data-testid="notification-settings">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Réglages notifications</h3>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-500 ml-1" />}
      </div>
      <p className="font-mono text-[11px] text-zinc-500 mb-5">Choisissez un son différent pour chaque évènement. Vos choix sont enregistrés sur votre compte et suivis sur tous vos appareils.</p>

      <PushSection />

      {/* Enable */}
      <div className="flex items-center justify-between py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-sm">
          {prefs.enabled ? <Bell className="w-4 h-4 text-signal-success" /> : <BellOff className="w-4 h-4 text-zinc-500" />}
          Son des notifications
        </div>
        <button data-testid="notif-enabled-toggle" onClick={() => update({ enabled: !prefs.enabled })}
          className={`relative w-12 h-6 rounded-full transition-colors ${prefs.enabled ? "bg-aurora-cyan" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${prefs.enabled ? "left-6" : "left-0.5"}`} />
        </button>
      </div>

      {/* Volume */}
      <div className={`py-4 border-b border-white/10 ${prefs.enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          <Volume2 className="w-4 h-4" /> Volume — {Math.round(prefs.volume * 100)}%
        </label>
        <input data-testid="notif-volume" type="range" min="0" max="1" step="0.05" value={prefs.volume}
          onChange={(e) => update({ volume: Number(e.target.value) })} className="w-full accent-aurora-cyan" />
      </div>

      {/* Per-event sounds */}
      <div className={`${prefs.enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 pt-4 mb-1 flex items-center gap-1.5">
          <Music className="w-3.5 h-3.5" /> Son par évènement
        </div>
        {NOTIF_EVENT_KEYS.map((k) => (
          <EventSoundRow key={k} kind={k} event={prefs.events[k]} volume={prefs.volume} onChange={setPrefs} />
        ))}
        <p className="font-mono text-[10px] text-zinc-500 mt-3">Astuce : un son court (1-2 s, ≤ 1 Mo) est idéal.</p>
      </div>

      {/* System notifications permission */}
      {perm !== "granted" && (
        <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <span className="font-mono text-[11px] text-zinc-500">Recevoir aussi les alertes système (hors onglet)</span>
          <button data-testid="notif-perm-btn" onClick={askPerm}
            className="px-3 py-2 rounded-sm border border-aurora-cyan/40 text-aurora-cyan text-sm hover:bg-aurora-cyan/10 transition-colors">
            Activer les notifications système
          </button>
        </div>
      )}
    </div>
  );
};
